/* DonorProfileDrawer — aggregated supporter relationship view.
 * Opens from the Unified Donations Ledger when a donor's name is clicked.
 * Loads via /api/admin/donors/profile and shows lifetime giving, funds
 * supported, event attendance, contribution timeline, and admin notes.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  X, Loader2, User, Mail, Heart, Calendar, TrendingUp, Save,
  Repeat, AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const SOURCE_COLOR = {
  stripe: 'bg-blue-50 text-blue-800',
  shop: 'bg-cyan-50 text-cyan-800',
  event_donation: 'bg-violet-50 text-violet-800',
  grant: 'bg-emerald-50 text-emerald-800',
  sponsorship: 'bg-indigo-50 text-indigo-800',
  check_donation: 'bg-amber-50 text-amber-800',
  cash_donation: 'bg-yellow-50 text-yellow-800',
  wire_transfer: 'bg-rose-50 text-rose-800',
  in_kind_donation: 'bg-pink-50 text-pink-800',
};

const DonorProfileDrawer = ({ open, donorName, donorEmail, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const load = useCallback(async () => {
    if (!open || (!donorName && !donorEmail)) return;
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (donorEmail) params.set('email', donorEmail);
      if (donorName) params.set('name', donorName);
      const r = await axios.get(`${BACKEND_URL}/api/admin/donors/profile?${params}`,
        { withCredentials: true });
      setData(r.data);
      setNotes(r.data?.notes || '');
    } catch (e) { setError(e.response?.data?.detail || 'Could not load profile.'); }
    setLoading(false);
  }, [open, donorName, donorEmail]);

  useEffect(() => { load(); }, [load]);

  const saveNotes = async () => {
    if (!data?.donor) return;
    setSavingNotes(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/donors/notes`, {
        name: data.donor.name,
        email: data.donor.email || null,
        notes,
      }, { withCredentials: true });
    } catch (e) { alert(e.response?.data?.detail || 'Save failed.'); }
    setSavingNotes(false);
  };

  if (!open) return null;
  const s = data?.summary || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="donor-profile-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] z-10 p-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Supporter Profile</p>
            <h2 className="font-heading text-xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
              <User className="w-5 h-5 text-[#BA1826]" /> {data?.donor?.name || donorName || 'Loading…'}
            </h2>
            {data?.donor?.email && (
              <p className="text-xs text-[#4B5563] inline-flex items-center gap-1 mt-0.5">
                <Mail className="w-3 h-3" /> {data.donor.email}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-[#4B5563] hover:text-[#0A0A0A]" data-testid="donor-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>}

          {!loading && data && (
            <>
              {/* Summary tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="donor-summary">
                <Tile icon={Heart} label="Lifetime giving" value={fmtMoney(s.total_amount)} accent="brand" />
                <Tile icon={TrendingUp} label="Tax-deductible" value={fmtMoney(s.deductible_amount)} accent="green" />
                <Tile icon={Calendar} label="Gift count" value={s.gift_count || 0} accent="indigo" />
                <Tile icon={Repeat} label="Cadence (days)" value={s.cadence_days != null ? s.cadence_days : '—'} accent="amber" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <FactBox label="First gift" value={s.first_gift ? s.first_gift.slice(0, 10) : '—'} />
                <FactBox label="Last gift" value={s.last_gift ? s.last_gift.slice(0, 10) : '—'} />
              </div>

              {/* Funds supported */}
              {data.funds_supported?.length > 0 && (
                <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="donor-funds">
                  <div className="bg-[#FAFBFC] px-3 py-2 border-b border-[#E5E7EB]">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">Funds Supported</p>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {data.funds_supported.map((f, i) => (
                        <tr key={i} className="border-t border-[#E5E7EB]">
                          <td className="px-3 py-1.5 font-mono font-bold w-20">{f.fund_code || '—'}</td>
                          <td className="px-3 py-1.5">{f.fund_name}</td>
                          <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtMoney(f.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Event attendance */}
              {data.event_attendance?.length > 0 && (
                <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="donor-events">
                  <div className="bg-[#FAFBFC] px-3 py-2 border-b border-[#E5E7EB]">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">Event Attendance</p>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {data.event_attendance.map((e, i) => (
                        <tr key={i} className="border-t border-[#E5E7EB]">
                          <td className="px-3 py-1.5 font-mono w-24">{(e.event_date || '').slice(0, 10) || '—'}</td>
                          <td className="px-3 py-1.5">{e.event_title}</td>
                          <td className="px-3 py-1.5 text-[10px] uppercase font-bold tracking-wider">
                            {e.checked_in ? <span className="text-green-700">Checked in</span> : <span className="text-[#9CA3AF]">{e.rsvp_status || 'RSVP'}</span>}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">{e.amount_paid > 0 ? fmtMoney(e.amount_paid) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* In-Kind Contributions — kept SEPARATE from monetary totals.
                  Estimated value never appears here; donor determines FMV. */}
              {(data.in_kind_donations?.length || 0) > 0 && (
                <div className="bg-white border border-pink-200 rounded-lg overflow-hidden" data-testid="donor-in-kind">
                  <div className="bg-pink-50 px-3 py-2 border-b border-pink-200 flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wider text-pink-800">In-Kind Contributions</p>
                    <span className="text-[10px] text-pink-800 font-mono">
                      {data.in_kind_summary?.count || 0} donation{(data.in_kind_summary?.count || 0) === 1 ? '' : 's'} · {data.in_kind_summary?.total_item_count || 0} items
                    </span>
                  </div>
                  <div className="text-[10px] text-pink-700 bg-pink-50/50 px-3 py-1.5 border-b border-pink-100">
                    Not included in monetary totals. Donor is responsible for determining fair market value.
                  </div>
                  {data.in_kind_donations.map((ik, i) => (
                    <div key={ik.id || i} className="border-t border-pink-100 px-3 py-2" data-testid={`donor-in-kind-row-${i}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-mono text-[10px] text-pink-700">{ik.receipt_number}</span>
                        <span className="font-mono text-[10px] text-[#9CA3AF]">{(ik.received_at || '').slice(0, 10)}</span>
                      </div>
                      <ul className="text-xs text-[#0A0A0A] space-y-0.5 pl-3">
                        {(ik.items || []).map((it, j) => (
                          <li key={j} className="flex items-baseline gap-2">
                            <span className="font-mono text-pink-700 w-8 text-right">{it.quantity}</span>
                            <span>{it.category}{it.description ? ` — ${it.description}` : ''}</span>
                            <span className="text-[10px] text-[#9CA3AF]">({({new:'New',like_new:'Like New',good:'Good',fair:'Fair'})[it.condition] || it.condition})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {/* Timeline */}
              {data.timeline?.length > 0 && (
                <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="donor-timeline">
                  <div className="bg-[#FAFBFC] px-3 py-2 border-b border-[#E5E7EB]">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">Contribution Timeline</p>
                  </div>
                  {data.timeline.map((t, i) => (
                    <div key={i} className="border-t border-[#E5E7EB] px-3 py-2 flex items-start gap-2"
                      data-testid={`donor-timeline-row-${i}`}>
                      <span className="font-mono text-[10px] text-[#9CA3AF] w-20 shrink-0">{(t.date || '').slice(0, 10) || '—'}</span>
                      <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded shrink-0 ${SOURCE_COLOR[t.source] || 'bg-gray-50 text-gray-700'}`}>
                        {t.source_label || t.source}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#0A0A0A] truncate">{t.memo || t.event_label || t.fund_label || t.payment_method}</p>
                        {t.fund_label && <p className="text-[10px] text-[#9CA3AF]">Fund: {t.fund_label}</p>}
                      </div>
                      <span className="font-mono font-bold text-xs shrink-0">{fmtMoney(t.amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              {data.timeline?.length === 0 && (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 text-center text-xs text-[#9CA3AF]"
                  data-testid="donor-empty">
                  <AlertCircle className="w-5 h-5 mx-auto mb-2 text-[#9CA3AF]" />
                  No contributions found for this supporter.
                </div>
              )}

              {/* Notes */}
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="donor-notes">
                <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1.5">Internal Notes</p>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={4} placeholder="Stewardship notes, communication history, relationship context…"
                  className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs"
                  data-testid="donor-notes-input" />
                <div className="text-right mt-1.5">
                  <button onClick={saveNotes} disabled={savingNotes}
                    className="text-[11px] px-3 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="donor-notes-save">
                    {savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Save notes
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ACCENT = {
  green: 'bg-green-50 border-green-200 text-green-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  brand: 'bg-[#FFF8F8] border-[#BA1826]/30 text-[#BA1826]',
};

const Tile = ({ icon: Icon, label, value, accent = 'brand' }) => (
  <div className={`border rounded p-2 ${ACCENT[accent]}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</span>
      <Icon className="w-3 h-3 opacity-70" />
    </div>
    <p className="font-heading text-base font-bold truncate" title={value}>{value}</p>
  </div>
);

const FactBox = ({ label, value }) => (
  <div className="bg-[#FAFBFC] border border-[#E5E7EB] rounded p-2">
    <p className="text-[9px] uppercase tracking-wider font-bold text-[#9CA3AF]">{label}</p>
    <p className="font-mono text-sm text-[#0A0A0A]">{value}</p>
  </div>
);

export default DonorProfileDrawer;
